import { loadScript } from './util';
import {
  WAF_CAPTCHA_API_KEY,
  OVERLAY_ID,
  MODAL_ID,
  FORM_ID,
} from './wafConfig';

// A single request sent through the AWS WAF CAPTCHA flow.
//
// The request is sent FIRST, carrying whatever token the visitor already holds,
// and a challenge is rendered only when the WAF answers with its CAPTCHA action.
// An earlier version challenged unconditionally before every submit, so a
// visitor who had already cleared a page-load interstitial was asked twice.
//
// This deliberately replaces the older `useAWSWAFCaptchaFetch`, which
// intercepted *every* fetch in the application and popped a challenge before
// each one. That made ordinary browsing unusable and is why the whole
// integration was removed in Aug 2024 (commit 501616e). Nothing here is global:
// callers opt in for one specific request, and today the only caller is the
// contact form.

/// The request currently in flight, or null. A second call while one is running
/// joins it instead of firing a second request and a second challenge.
let inFlight = null;

function show(id, display) {
  const element = document.getElementById(id);
  if (element) element.style.display = display;
}

function openModal() {
  document.body.style.cursor = 'wait';
  show(OVERLAY_ID, 'block');
  // `flex`, not `block`: the modal's centring rules in AWSWAFCaptchaModal.css
  // only apply to a flex container, and the card must be sized before render.
  show(MODAL_ID, 'flex');
}

function closeModal() {
  document.body.style.cursor = 'default';
  show(OVERLAY_ID, 'none');
  show(MODAL_ID, 'none');
}

/// True when the WAF answered by asking for a CAPTCHA.
///
/// Both the status AND the header are checked. ASP.NET returns a bare 405 of its
/// own for a method mismatch against `SmtpController`, which is `[HttpPost]`-only,
/// and rendering a puzzle for that would be an unsolvable dead end: solving it
/// changes nothing and the retry 405s again. The accepted consequence of keying
/// on the header is the other direction - if the WAF ever stops sending
/// `x-amzn-waf-action`, the form surfaces the 405 to the caller instead of
/// offering a puzzle.
function isCaptchaChallenge(response) {
  return (
    response.status === 405 &&
    response.headers?.get?.('x-amzn-waf-action') === 'captcha'
  );
}

/// Renders the challenge and resolves once the visitor solves it.
function challenge() {
  return new Promise((resolve, reject) => {
    const container = document.getElementById(FORM_ID);
    if (!container) {
      reject(new Error('CAPTCHA container is not mounted.'));
      return;
    }

    // NOTE: the API is `renderCaptcha`, lower-case "c". The disabled snippet
    // left behind in ContactUs.js called `renderCaptCha`, which does not exist.
    window.AwsWafCaptcha.renderCaptcha(container, {
      apiKey: WAF_CAPTCHA_API_KEY,
      onSuccess: () => resolve(),
      onError: (err) => reject(err instanceof Error ? err : new Error('CAPTCHA failed.')),
      onLoad: () => { document.body.style.cursor = 'default'; },
      dynamicWidth: true,
      skipTitle: false,
    });
  });
}

async function run(input, init) {
  // The probe request below can take a moment and has no visible affordance of
  // its own, so the busy cursor covers the whole call, not just the challenge.
  document.body.style.cursor = 'wait';
  try {
    await loadScript();

    // `AwsWafIntegration.fetch` is the SDK's wrapper: it attaches the token the
    // visitor already holds, which is what the WAF rule on the endpoint checks
    // for. A plain `fetch` would never carry one.
    let response = await window.AwsWafIntegration.fetch(input, init);
    if (!isCaptchaChallenge(response)) return response;

    openModal();
    try {
      await challenge();
    } finally {
      closeModal();
    }

    // Exactly one retry. The visitor has just solved a puzzle, so a second
    // CAPTCHA answer means the token was not accepted (expired, wrong token
    // domain, a WAF rule the puzzle cannot satisfy) and looping would render
    // puzzle after puzzle without ever sending the message. Fail with something
    // the visitor can act on instead.
    response = await window.AwsWafIntegration.fetch(input, { ...init });
    if (isCaptchaChallenge(response)) {
      throw new Error(
        'We could not verify that you are human. Please click Submit to try again.'
      );
    }

    return response;
  } finally {
    document.body.style.cursor = 'default';
  }
}

/// Performs one fetch, rendering a CAPTCHA only if the WAF asks for one.
///
/// The request goes out first with the token the visitor already holds. If the
/// WAF answers with its CAPTCHA action (HTTP 405 plus `x-amzn-waf-action:
/// captcha`) the challenge is rendered and the request is retried once; any
/// other response is handed back to the caller untouched, with no modal shown.
///
/// `init.body` must therefore be re-sendable - a string, `FormData` or `Blob`,
/// not a stream - because a CAPTCHA response causes exactly one retry with that
/// same `init`. A consumed stream body would send an empty second request.
///
/// Concurrent calls share one request: while a call is in flight, another
/// returns the same promise rather than starting a second submit and a second
/// challenge.
export function captchaFetch(input, init) {
  if (inFlight) return inFlight;

  inFlight = run(input, init).finally(() => {
    inFlight = null;
  });

  return inFlight;
}
